// src/components/communication/QuickOrderViewModal.js
import React, { useState, useEffect } from "react";
import {
  X,
  Package,
  User,
  MapPin,
  Truck,
  Calendar,
  Clock,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Tag,
  Loader,
  FileText,
  ShieldCheck,
  Printer,
  Ruler,
  Gem,
  PenTool,
  Layers,
  Sparkles,
  Phone,
  Siren,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { useCommunication } from "../../contexts/CommunicationContext";
import { getBusinessDaysDiff } from "../../utils/formatters";
import PrazoAnalysis from "../production/PrazoAnalysis";
import PrazoDetalhesModal from "../production/PrazoDetalhesModal";
import EngravingDisplay from "../production/EngravingDisplay";
import { getEngravingBadgeStyle } from "../../utils/engravingUtils";

// Helper Anti-ND
const hasValue = (val) => {
  if (!val) return false;
  const s = String(val).trim().toUpperCase();
  return s !== "" && s !== "ND" && s !== "-" && s !== "N/A" && s !== "NULL" && s !== "UNDEFINED";
};

export default function QuickOrderViewModal({ onNavigateTab }) {
  const { quickOrderNumber, closeQuickOrder, openLightbox, openQuickProduct } = useCommunication();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [prazoModalOpen, setPrazoModalOpen] = useState(false);

  useEffect(() => {
    if (!quickOrderNumber) {
      setOrder(null);
      setError(null);
      return;
    }

    const fetchOrder = async () => {
      setLoading(true);
      setError(null);
      try {
        const cleanNum = String(quickOrderNumber).replace(/[[\]#]/g, "").trim();

        // 1. Busca todos os registros do pedido em production_orders
        const [records, invoices] = await Promise.all([
          pb.collection("production_orders").getFullList({
            filter: `order_number = "${cleanNum}" || id = "${cleanNum}"`,
            expand: "fiscal_invoices_via_order",
          }),
          pb.collection("fiscal_invoices").getFullList({
            filter: `order_number = "${cleanNum}"`,
            sort: "-created",
            limit: 1,
          }).catch(() => []),
        ]);

        if (records && records.length > 0) {
          const first = records[0];
          const full = first.full_data || {};

          // 2. Extrai Itens e Características Produtivas Detalhadas
          const extractedItems = [];
          records.forEach((r, idx) => {
            const rFull = r.full_data || {};
            const specs = r.specs || rFull.specs || {};

            // Medida / Aro
            const size =
              specs.size ||
              specs.Tamanho ||
              specs.Aro ||
              specs.aro ||
              specs.tamanho ||
              specs.medida ||
              specs.comprimento ||
              rFull.size ||
              "";

            // Metal / Banho / Cor
            const metal =
              specs.metal ||
              specs.banho ||
              specs.cor ||
              specs.material ||
              specs.banho_cor ||
              rFull.metal ||
              rFull.banho ||
              "";

            // Pedra / Cravação
            const stoneType =
              specs.stoneType ||
              specs.pedra ||
              specs.tipo_pedra ||
              specs.cravacao ||
              specs.tipo_cravacao ||
              rFull.stoneType ||
              "";

            const stoneColor =
              specs.stoneColor ||
              specs.cor_pedra ||
              specs.stone_color ||
              rFull.stoneColor ||
              "";

            const stoneBatch = specs.stoneBatch || specs.lote_pedra || "";
            const isNatural =
              String(stoneType).toLowerCase().includes("natural") ||
              String(r.name || "").toLowerCase().includes("natural") ||
              Boolean(specs.isNatural);

            // Gravação
            const engraving =
              specs.engraving ||
              specs.gravacao ||
              specs.texto_gravacao ||
              specs.texto ||
              rFull.engraving ||
              "";

            const engravingFont = specs.engravingFont || specs.fonte || "";

            // Exceção de Qualidade
            const exception =
              r.exception ||
              specs.exception ||
              rFull.exception ||
              "";

            // Etapa de Produção
            const step =
              r.production_step ||
              r.status ||
              rFull.productionStep ||
              rFull.status ||
              "Aguardando";

            // Foto
            const image =
              r.image ||
              rFull.image ||
              rFull.product?.image ||
              rFull.item?.image ||
              rFull.foto ||
              "";

            // SKU e Nome
            const sku = r.sku || rFull.sku || rFull.product?.sku || rFull.item?.sku || "";
            const name =
              r.product_name ||
              r.name ||
              rFull.name ||
              rFull.title ||
              rFull.product?.name ||
              rFull.item?.name ||
              (sku ? `Peça (${sku})` : `Item #${idx + 1}`);

            // Certificado impresso neste item
            const itemCertPrinted = Boolean(r.certificatePrinted || rFull.certificatePrinted);
            const itemLabelPrinted = Boolean(r.printed || rFull.printed);

            extractedItems.push({
              id: r.id,
              sku,
              name,
              image,
              quantity: r.quantity || r.qtd || rFull.quantity || 1,
              specs,
              size,
              metal,
              stoneType,
              stoneColor,
              stoneBatch,
              isNatural,
              engraving,
              engravingFont,
              exception,
              step,
              certificatePrinted: itemCertPrinted,
              printed: itemLabelPrinted,
              fromStock: Boolean(r.fromStock || rFull.fromStock),
              isPE: Boolean(r.isPE || rFull.isPE),
              isIntercepted: Boolean(r.isInterceptedPE || rFull.isInterceptedPE),
              isInterceptedPE: Boolean(r.isInterceptedPE || rFull.isInterceptedPE),
              price: r.price || rFull.price || 0,
              status: r.production_step || r.status || step,
              customCreatedAt: r.customCreatedAt || r.created,
              createdAt: r.createdAt || r.created,
              messages: rFull.messages || r.messages,
              full_data: rFull,
            });
          });

          // 3. Documentos e Status Operacionais
          // Nota Fiscal
          const activeInvoice =
            (invoices && invoices.length > 0 ? invoices[0] : null) ||
            (first.expand?.fiscal_invoices_via_order && first.expand.fiscal_invoices_via_order.length > 0
              ? first.expand.fiscal_invoices_via_order[0]
              : null);

          const isNfeEmitted =
            activeInvoice?.nfe_status === "autorizada" ||
            Boolean(activeInvoice?.nfe_numero) ||
            Boolean(full.nfe?.number) ||
            Boolean(first.nfe_numero);

          const nfeNumber =
            activeInvoice?.nfe_numero ||
            full.nfe?.number ||
            first.nfe_numero ||
            "";

          // Certificado de Garantia
          const isCertPrinted = records.some(
            (r) => r.certificatePrinted || r.full_data?.certificatePrinted
          );

          // Etiqueta de Envio / Rastreio
          const tracking =
            full.correios_codigo_objeto ||
            full.shipping?.tracking ||
            first.tracking ||
            "";

          const isLabelPrinted =
            Boolean(tracking) ||
            records.some((r) => r.printed || r.full_data?.printed || r.full_data?.etiqueta_impressa);

          // Endereço e Cliente
          const customerName =
            first.customer_name ||
            full.customer_name ||
            full.order?.customer?.name ||
            full.order?.customer_name ||
            "Cliente Balcão";

          const customerPhone =
            full.order?.customer?.phone ||
            full.customer_phone ||
            full.shipping?.address?.phone ||
            "";

          const shippingCity =
            first.shipping_city ||
            full.shipping_city ||
            full.shipping?.address?.city ||
            full.order?.shipping_address?.city ||
            "";

          const shippingState =
            first.shipping_state ||
            full.shipping_state ||
            full.shipping?.address?.statecode ||
            full.shipping?.address?.state ||
            full.order?.shipping_address?.state ||
            "";

          const shippingMethod =
            full.shipping?.tipoenvio ||
            full.shipping?.method ||
            full.shipping_method ||
            "Correios / Padrão";

          const logisticsStatus =
            first.logistics_status ||
            full.logisticsStatus ||
            full.logistics_status ||
            "SEM ETIQUETA";

          const productionStep =
            first.production_step ||
            full.productionStep ||
            full.production_step ||
            "Aguardando";

          setOrder({
            id: first.id,
            order_number: first.order_number || full.order?.number || cleanNum,
            customer_name: customerName,
            customer_phone: customerPhone,
            logistics_status: logisticsStatus,
            production_step: productionStep,
            shipping_city: shippingCity,
            shipping_state: shippingState,
            shipping_method: shippingMethod,
            tracking: tracking,
            isNfeEmitted,
            nfeNumber,
            isCertPrinted,
            isLabelPrinted,
            items: extractedItems,
            notes: full.notes || full.obs || first.notes || "",
            created:
              first.customCreatedAt ||
              first.created_at ||
              full.order?.created_at ||
              first.created ||
              full.created_at ||
              first.updated,
            rawOrder: first,
          });
        } else {
          setError(`Pedido #${cleanNum} não encontrado no sistema.`);
        }
      } catch (err) {
        console.error("Erro ao buscar pedido rápido:", err);
        setError("Erro ao carregar detalhes do pedido.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [quickOrderNumber]);

  if (!quickOrderNumber) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-scaleUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ================= 1. HEADER DO PEDIDO ================= */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-300">
              <Package size={22} />
            </div>
            <div>
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="font-bold text-lg tracking-wide text-white">
                  Pedido #{quickOrderNumber}
                </span>
                {order && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                    {order.logistics_status}
                  </span>
                )}
                {order && order.created && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setPrazoModalOpen(true)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border shadow-xs cursor-pointer hover:scale-105 active:scale-95 transition-all select-none ${
                      (() => {
                        const days = Math.floor(getBusinessDaysDiff(order.created));
                        if (days >= 9) return "bg-purple-900/90 text-purple-100 border-purple-400/40 ring-1 ring-purple-400/30";
                        if (days >= 8) return "bg-rose-500/30 text-rose-200 border-rose-400/40 animate-pulse";
                        if (days >= 5) return "bg-amber-500/30 text-amber-200 border-amber-400/40";
                        return "bg-emerald-500/25 text-emerald-200 border-emerald-400/40";
                      })()
                    }`}
                    title={`Data de entrada: ${new Date(order.created).toLocaleDateString("pt-BR")} (Clique para abrir Análise Temporal & Esteira)`}
                  >
                    <Clock size={12} />
                    <span>
                      {(() => {
                        const days = Math.floor(getBusinessDaysDiff(order.created));
                        return `${days > 9 ? "+9" : days} ${days === 1 ? "dia útil" : "dias úteis"}`;
                      })()}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400 flex items-center gap-2">
                <span>Ficha Rápida & Características Produtivas</span>
                {order?.created && (
                  <span className="text-slate-400 text-[11px] opacity-80">
                    • Entrada: {new Date(order.created).toLocaleDateString("pt-BR")}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={closeQuickOrder}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* ================= 2. CORPO DETALHADO ================= */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1 custom-scrollbar">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 gap-3">
              <Loader className="animate-spin text-indigo-600" size={32} />
              <span className="text-sm font-medium">Buscando dados e especificações do pedido #{quickOrderNumber}...</span>
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 flex items-center gap-3">
              <AlertCircle size={20} className="shrink-0" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          {order && !loading && (
            <>
              {/* === ANÁLISE TEMPORAL DE PRAZO DA OFICINA === */}
              <PrazoAnalysis
                order={{
                  ...(order.rawOrder || {}),
                  ...order,
                  items: order.items,
                  orderNumber: order.order_number,
                }}
              />

              {/* === ETAPA & DOCUMENTOS (CHECKLIST OPERACIONAL) === */}
              <div className="p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-indigo-50/30 border border-slate-200/90 space-y-3">
                <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-700 uppercase tracking-wider">
                    <Truck size={14} className="text-indigo-600" />
                    Etapa & Checklist de Documentos
                  </div>
                  <span className="text-xs font-bold text-indigo-700 bg-indigo-100/80 px-2.5 py-0.5 rounded-lg">
                    Etapa Atual: {order.production_step}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {/* 📜 CERTIFICADO */}
                  <div className="p-2.5 rounded-xl bg-white border border-slate-200 flex items-center gap-2.5 shadow-xs">
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs shrink-0 ${
                        order.isCertPrinted
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      <ShieldCheck size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold text-slate-700">Certificado</p>
                      <span
                        className={`text-[10px] font-bold ${
                          order.isCertPrinted ? "text-emerald-600" : "text-slate-400"
                        }`}
                      >
                        {order.isCertPrinted ? "✓ Impresso" : "○ Pendente"}
                      </span>
                    </div>
                  </div>

                  {/* 🧾 NOTA FISCAL */}
                  <div className="p-2.5 rounded-xl bg-white border border-slate-200 flex items-center gap-2.5 shadow-xs">
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs shrink-0 ${
                        order.isNfeEmitted
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      <FileText size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold text-slate-700">Nota Fiscal</p>
                      <span
                        className={`text-[10px] font-bold truncate block ${
                          order.isNfeEmitted ? "text-emerald-600" : "text-slate-400"
                        }`}
                      >
                        {order.isNfeEmitted
                          ? `✓ Emitida ${order.nfeNumber ? `#${order.nfeNumber}` : ""}`
                          : "○ Pendente"}
                      </span>
                    </div>
                  </div>

                  {/* 🏷️ ETIQUETA / RASTREIO */}
                  <div className="p-2.5 rounded-xl bg-white border border-slate-200 flex items-center gap-2.5 shadow-xs">
                    <div
                      className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs shrink-0 ${
                        order.isLabelPrinted
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      <Printer size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-bold text-slate-700">Etiqueta Envio</p>
                      <span
                        className={`text-[10px] font-bold truncate block ${
                          order.isLabelPrinted ? "text-emerald-600" : "text-slate-400"
                        }`}
                      >
                        {order.tracking ? `✓ ${order.tracking}` : order.isLabelPrinted ? "✓ Impressa" : "○ Sem Etiqueta"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* === CLIENTE & LOCALIZAÇÃO === */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                  <div className="flex items-center gap-1.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                    <User size={13} />
                    Cliente
                  </div>
                  <p className="text-sm font-bold text-slate-800 truncate">{order.customer_name}</p>
                  {order.customer_phone && (
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Phone size={11} />
                      {order.customer_phone}
                    </p>
                  )}
                </div>

                <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
                  <div className="flex items-center gap-1.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">
                    <MapPin size={13} />
                    Destino & Envio
                  </div>
                  <p className="text-sm font-bold text-slate-800 truncate">
                    {order.shipping_city ? `${order.shipping_city} - ${order.shipping_state}` : "Retirada Balcão"}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    Modalidade: {order.shipping_method}
                  </p>
                </div>
              </div>

              {/* === ITENS & CARACTERÍSTICAS PRODUTIVAS DETALHADAS === */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Tag size={14} className="text-indigo-600" />
                    Itens do Pedido & Especificações Técnicas ({order.items?.length || 0})
                  </span>
                </h4>

                <div className="space-y-3">
                  {order.items.map((item, idx) => {
                    const imgUrl = item.image || "";

                    return (
                      <div
                        key={item.id || idx}
                        className="p-3.5 rounded-2xl bg-white border border-slate-200 hover:border-indigo-200 transition shadow-xs space-y-3"
                      >
                        {/* CABEÇALHO DO ITEM */}
                        <div className="flex items-start gap-3">
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={item.name}
                              onClick={() => openLightbox(imgUrl, item.name)}
                              className="w-14 h-14 rounded-xl object-cover border border-slate-200 shrink-0 cursor-pointer hover:scale-105 transition shadow-2xs"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 shrink-0">
                              <Package size={22} />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h5 className="text-xs font-bold text-slate-800 leading-tight">
                                {item.name}
                              </h5>
                              <span className="text-[11px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md shrink-0">
                                {item.step}
                              </span>
                            </div>

                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1 flex-wrap">
                              {item.sku && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openQuickProduct(item.sku);
                                  }}
                                  className="inline-flex items-center gap-1 font-mono font-bold bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 hover:border-emerald-300 px-2 py-0.5 rounded-lg text-xs transition cursor-pointer shadow-2xs hover:scale-105 active:scale-95"
                                  title={`Clique para ver a Ficha do Produto (${item.sku})`}
                                >
                                  <Gem size={11} className="text-emerald-600" />
                                  <span>{item.sku}</span>
                                  <ExternalLink size={10} className="text-emerald-500 opacity-70 ml-0.5" />
                                </button>
                              )}
                              <span>Qtd: {item.quantity}</span>
                              {item.isNatural && (
                                <span className="bg-indigo-50 text-indigo-700 font-bold px-1.5 py-0.5 rounded text-[10px] flex items-center gap-1 border border-indigo-100">
                                  <Sparkles size={10} /> Pedra Natural
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* ALERTA DE EXCEÇÃO DE QUALIDADE (SE HOUVER) */}
                        {hasValue(item.exception) && (
                          <div className="p-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold flex items-center gap-2 animate-pulse">
                            <Siren size={14} className="shrink-0 text-rose-600" />
                            <span>EXCEÇÃO: {item.exception}</span>
                          </div>
                        )}

                        {/* GRID DE CARACTERÍSTICAS PRODUTIVAS */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 border-t border-slate-100 text-xs">
                          {/* 1. Medida / Aro */}
                          <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-0.5">
                              <Ruler size={10} className="text-slate-500" />
                              Medida / Aro
                            </span>
                            <p className="font-bold text-slate-800 truncate">
                              {hasValue(item.size) ? item.size : "Padrão"}
                            </p>
                          </div>

                          {/* 2. Metal / Banho */}
                          <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-0.5">
                              <Layers size={10} className="text-slate-500" />
                              Metal / Banho
                            </span>
                            <p className="font-bold text-slate-800 truncate">
                              {hasValue(item.metal) ? item.metal : "Prata 925"}
                            </p>
                          </div>

                          {/* 3. Pedra / Cor */}
                          <div className="p-2 rounded-xl bg-slate-50 border border-slate-100">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1 mb-0.5">
                              <Gem size={10} className="text-purple-500" />
                              Pedra / Cor
                            </span>
                            <p className="font-bold text-slate-800 truncate">
                              {hasValue(item.stoneColor)
                                ? item.stoneColor
                                : hasValue(item.stoneType)
                                ? item.stoneType
                                : "Branca / Zircônia"}
                            </p>
                          </div>

                          {/* 4. Gravação */}
                          <div className="p-2 rounded-xl bg-purple-50/60 border border-purple-100">
                            <div className="flex items-center justify-between gap-1 mb-0.5">
                              <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider flex items-center gap-1">
                                <PenTool size={10} className="text-purple-500" />
                                Gravação
                              </span>
                              {hasValue(item.engraving) && (
                                <span
                                  className={`text-[9px] px-1.5 py-0.2 rounded-full border ${
                                    getEngravingBadgeStyle(item.engraving.length).badgeClass
                                  }`}
                                >
                                  {item.engraving.length} carac.
                                </span>
                              )}
                            </div>
                            <p className="font-bold text-purple-900 truncate" title={item.engraving || "Sem gravação"}>
                              {hasValue(item.engraving) ? `"${item.engraving}"` : "Sem gravação"}
                            </p>
                          </div>
                        </div>

                        {/* DETALHAMENTO DA GRAVAÇÃO PARA O LASER (COM BOTÃO DE CÓPIA POR PEÇA) */}
                        {hasValue(item.engraving) && (
                          <div className="mt-2.5 pt-2 border-t border-purple-100/80">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-[10px] font-extrabold text-purple-900 uppercase tracking-wider flex items-center gap-1.5">
                                <PenTool size={11} className="text-purple-600" />
                                Especificação para Gravação a Laser
                              </span>
                              <span className="text-[10px] text-purple-600 font-medium">
                                Copie individualmente para o software
                              </span>
                            </div>
                            <EngravingDisplay rawText={item.engraving} mode="detailed" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* OBSERVAÇÕES */}
              {order.notes && (
                <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs space-y-1">
                  <span className="font-bold uppercase tracking-wider text-[10px] text-amber-700 block">
                    Observações do Pedido
                  </span>
                  <p className="leading-relaxed">{order.notes}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* ================= 3. FOOTER ================= */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
          <button
            onClick={closeQuickOrder}
            className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 transition cursor-pointer"
          >
            Fechar
          </button>

          {order && onNavigateTab && (
            <button
              onClick={() => {
                closeQuickOrder();
                onNavigateTab("orders");
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-sm shadow-indigo-600/30 transition active:scale-95 cursor-pointer"
            >
              <span>Abrir na Aba de Pedidos</span>
              <ExternalLink size={13} />
            </button>
          )}
        </div>
      </div>

      {/* MODAL DETALHADO DE ANÁLISE TEMPORAL DE PRAZOS & ESTEIRA */}
      {prazoModalOpen && order && (
        <PrazoDetalhesModal
          order={{
            ...(order.rawOrder || {}),
            ...order,
            items: order.items,
            orderNumber: order.order_number,
          }}
          isOpen={prazoModalOpen}
          onClose={() => setPrazoModalOpen(false)}
        />
      )}
    </div>
  );
}
